// MapComponent.js
import React, { useContext, useEffect, useRef, useState } from "react";
// import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import {
  BookNow,
  DetailSection,
  Header,
  HeaderRightbar,
  HotelImage,
  InfoColumn,
  InfoText,
  RightSidebar,
  SSelect,
  StyledInfoWindow,
  MotionWrapper,
  WrapperForm,
} from "./styled";
import { hotelCountries } from "../../constants/hotelCountries";
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map,
} from "@vis.gl/react-google-maps";
import CustomSquareMarker from "./CustomerMarker";
import { ReactComponent as DoubleChevronRight } from "../../assets/double-chevron-right.svg";
import BookingForm from "./BookingForm";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import SubscriptionForm from "./SubscriptionForm";
import { LocationCityOutlined, AttachMoneyOutlined } from "@mui/icons-material";
import { getUserInfoThunk } from "../../store/actions/auth.action";
import { Hotel } from "./types";
import { HeaderContext } from "../MainLayout";
import AuthGuard from "../../components/AuthGuard";

const HotelMarker = ({
  hotel,
  onClick,
}: {
  hotel: Hotel;
  onClick: () => void;
}) => {
  return (
    <AdvancedMarker
      position={{ lat: hotel.lat, lng: hotel.lng }}
      onClick={onClick}
    >
      <div
        style={{
          position: "absolute",
          transform: "translate(-50%, -100%)",
          left: "50%",
          top: "50%",
        }}
        onClick={onClick}
      >
        <CustomSquareMarker logo={hotel.logo} />
      </div>
    </AdvancedMarker>
  );
};

const BookingPage = () => {
  const { setIsRightSideOpen } = useContext(HeaderContext);
  const { me } = useAppSelector((state: RootState) => state.authReducer);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [hotelInfo, setHotelInfo] = useState<any | null>(null);
  const [bookingFormData, setBookingFormData] = useState<any | null>(null);
  const dispatch = useAppDispatch();

  const fetchUser = async () => {
    try {
      await dispatch(getUserInfoThunk()).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // Use the first hotel's coordinates as the initial center
  const initialCenter = {
    lat: 38.856614,
    lng: 20.3522219,
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    setIsRightSideOpen?.(isSidebarOpen);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleCloseRightSideBar = () => {
      setIsSidebarOpen(false);
    };

    window.addEventListener("open-left-sidebar", handleCloseRightSideBar);

    return () => {
      window.removeEventListener("open-left-sidebar", handleCloseRightSideBar);
    };
  }, [setIsSidebarOpen]);

  return (
    <MotionWrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      isOpen={isSidebarOpen}
      isAuth={!!me}
    >
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}>
        <Map
          defaultCenter={initialCenter}
          gestureHandling={"greedy"}
          disableDefaultUI={true}
          mapId={"95f882a45d3a706"}
          defaultZoom={3}
          minZoom={3}
          restriction={{
            latLngBounds: {
              north: 85,
              south: -85,
              west: -180,
              east: 180,
            },
            strictBounds: true,
          }}
        >
          {/* Render all hotels from all countries */}
          {hotelCountries.map((country) =>
            country.cities.map((city) =>
              city.hotels.map((hotel, index) => (
                <HotelMarker
                  key={`${country.value}-${city.value}-${index}`}
                  hotel={hotel}
                  onClick={() => {
                    setSelectedHotel(hotel);
                    setShowInfoWindow(true);
                    setHotelInfo({
                      country: country,
                      city: city,
                    });
                  }}
                />
              ))
            )
          )}

          {showInfoWindow && selectedHotel && (
            <InfoWindow
              headerContent={<Header>{selectedHotel.label}</Header>}
              position={{
                lat: selectedHotel.lat,
                lng: selectedHotel.lng,
              }}
              pixelOffset={[0, -50]}
              onClose={() => setShowInfoWindow(false)}
              style={{ padding: 0 }}
            >
              <StyledInfoWindow>
                <HotelImage src={selectedHotel.image} alt="logo" />
                <InfoColumn>
                  <DetailSection>
                    <InfoText>
                      <AttachMoneyOutlined />
                      <p>
                        Starting from
                        <b>
                          {selectedHotel.currency || "USD"}{" "}
                          {selectedHotel.startPrice.toLocaleString("en-US", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </b>
                      </p>
                    </InfoText>
                    <InfoText>
                      <LocationCityOutlined />
                      <p>{selectedHotel.address}</p>
                    </InfoText>
                  </DetailSection>
                  <AuthGuard>
                    <BookNow
                      onClick={() => {
                        if (!me) return;
                        setIsSidebarOpen(true);
                        setBookingFormData({
                          hotel: selectedHotel,
                          country: hotelInfo?.country,
                          city: hotelInfo?.city,
                        });
                      }}
                    >
                      Book Now
                    </BookNow>
                  </AuthGuard>
                </InfoColumn>
              </StyledInfoWindow>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>

      <RightSidebar isOpen={isSidebarOpen} isAuth={!!me}>
        <HeaderRightbar>
          <div style={{ position: "absolute" }}>
            {isSidebarOpen && (
              <DoubleChevronRight width={14} onClick={toggleSidebar} />
            )}
          </div>
          <h3 style={{ margin: 0, textAlign: "center", width: "100%" }}>
            Booking
          </h3>
        </HeaderRightbar>
        {me?.paymentMode === 4 && bookingFormData && (
          <WrapperForm isAuth={!!me}>
            <BookingForm defaultHotel={bookingFormData} />
          </WrapperForm>
        )}
        {me?.paymentMode !== 4 && (
          <div style={{ padding: "2rem" }}>
            <SubscriptionForm />
          </div>
        )}
      </RightSidebar>
    </MotionWrapper>
  );
};

export default BookingPage;
